import { inject } from '@angular/core';
import { ResolveFn } from '@angular/router';

import { of } from 'rxjs';
import { first, map, tap, withLatestFrom } from 'rxjs/operators';

import { Store } from '@ngrx/store';

import { EnvironmentFeatures } from '@era/app';
import { EnvironmentService } from '@methylium/core';
import { CategoryStoreActions } from '../category';
import { LocationsRouterStoreSelectors, RouterStoreStates } from '../router/index';
import { CategoryType } from '../shared/types';

export const categoryResolver: (categoryType: CategoryType) => ResolveFn<boolean> = (categoryType: CategoryType) => {
  return () => {
    const routerStore = inject(Store<RouterStoreStates.RouterState>);
    const categoryStore = inject(Store);
    const environmentService = inject(EnvironmentService);
    const features: EnvironmentFeatures = environmentService.environment.features;

    // Check feature flags for categories that require them
    const featureEnabled = (() => {
      switch (categoryType) {
        case 'Bar':
          return features.bar?.enabled ?? true;
        case 'FrontDesk':
          return features.frontdesk?.enabled ?? true;
        case 'Housekeeping':
          return features.housekeeping?.enabled ?? true;
        case 'Kiosk':
          return features.kiosk?.enabled ?? true;
        case 'Staff':
          return features.staff?.enabled ?? true;
        case 'Location':
        case 'Room':
          return true; // No feature flag check for Location and Room
        default:
          return true;
      }
    })();

    if (!featureEnabled) {
      return of(true);
    }

    // Handle Brand: uses brandId, dispatches categoriesRequested
    if (categoryType === 'Brand') {
      return routerStore.select(LocationsRouterStoreSelectors.selectBrandHumanId).pipe(
        tap(() => {
          categoryStore.dispatch(
            CategoryStoreActions.CategoryResolverActions.categoriesRequested({
              categoryType: 'Brand',
            })
          );
        }),
        map(() => true),
        first()
      );
    }

    // Handle Location: uses brandId, dispatches categoriesRequested
    if (categoryType === 'Location') {
      return routerStore.select(LocationsRouterStoreSelectors.selectBrandHumanId).pipe(
        tap(() => {
          categoryStore.dispatch(
            CategoryStoreActions.CategoryResolverActions.categoriesRequested({
              categoryType: 'Location',
            })
          );
        }),
        map(() => true),
        first()
      );
    }

    // Handle Room: uses locationId and brandId, dispatches categoriesRequested
    if (categoryType === 'Room') {
      return routerStore.select(LocationsRouterStoreSelectors.selectLocationHumanId).pipe(
        withLatestFrom(routerStore.select(LocationsRouterStoreSelectors.selectBrandHumanId)),
        tap(([locationHumanId]) => {
          categoryStore.dispatch(
            CategoryStoreActions.CategoryResolverActions.categoriesRequested({
              categoryType: 'Room',
              locationId: locationHumanId,
            })
          );
        }),
        map(() => true),
        first()
      );
    }

    // Handle Bar, FrontDesk, Housekeeping, Kiosk, Staff: use locationId, dispatch categoryRequested
    return routerStore.select(LocationsRouterStoreSelectors.selectLocationHumanId).pipe(
      tap((locationId) => {
        categoryStore.dispatch(
          CategoryStoreActions.CategoryResolverActions.categoriesRequested({
            categoryType,
            locationId,
          })
        );
      }),
      map(() => true),
      first()
    );
  };
};
